import React, { useState } from 'react';
import { Package, Loader2, Edit, Trash2, Copy, Upload, CheckCircle, Clock, User, ClipboardList } from 'lucide-react';
import { Badge } from '../../../../components/ui/Badge';
import { PlansEditModal } from './PlansEditModal';
import { PlansProcedures } from './PlansProcedures';
import { PlansSpareParts } from './PlansSpareParts';
import { WorkOrdersEditModal } from '../work-orders/WorkOrdersEditModal';
import { WorkOrdersTasks } from '../work-orders/WorkOrdersTasks';
import { WorkOrdersAssignments } from '../work-orders/WorkOrdersAssignments';

export const PlansDetails = ({ product, mode = 'maintPlan', maintenancePlan, loading, onDelete, onSaveUpdate, onSaveDuplicate }) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [editMode, setEditMode] = useState('edit');
  const [activeTab, setActiveTab] = useState('details');

  //Asset readable names
  let nombres = {
    nombre_asset: product?.asset_location || null,
    nombre_sensor_asset: product?.sensor_asset_location || null,
  };

  //Turn timestamp format to dd/mm/yyyy hh:mm
  const dateFormat = (string) => {
    if (!string) return null;
    const date = string.split('T')[0];
    const clock = string.split('T')[1];
    const [year, month, day] = date.split('-');
    const [hour, minute] = clock.split(':');
    return `${day}/${month}/${year} ${hour}:${minute}`;
  };

  //Loading screen
  if (loading) {
    return (
      <div className="rounded-2xl border border-gray-700 bg-gray-800 shadow-lg p-8 min-h-0 max-h-[700px] flex items-center justify-center">
        <div className="flex flex-col items-center text-gray-400">
          <Loader2 className="animate-spin mb-4" size={32} />
          {mode !== 'WO' ? <p>Loading plan details...</p> : <p>Loading work order details...</p>}
        </div>
      </div>
    );
  }

  //Empty Details
  if (!product) {
    return (
      <div className="rounded-2xl border border-gray-700 bg-gray-800 shadow-lg p-8 min-h-0 max-h-[700px] flex items-center justify-center">
        <div className="text-center text-gray-400">
          <Package className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p>Select a {mode !== 'WO' ? 'plan' : 'work order'} to view its details</p>
        </div>
      </div>
    );
  }

  // We reuse the same component to show both Maintenance Plans details and Work Orders Details, that's why we have the 'mode' prop.
  return (
    <>
      <div className="rounded-2xl border border-gray-700 bg-gray-800 shadow-lg flex flex-col max-h-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <Package className="w-5 h-5 text-gray-400" />
            <h2 className="text-xl font-semibold text-white">
              {mode !== 'WO' ? 'Plan' : 'Work Order'} Details
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {/* Edit Button */}
            {product && (
              <button
                type="button"
                onClick={() => {
                  setEditMode('edit');
                  setIsEditModalOpen(true);
                }}
                className="inline-flex items-center justify-center p-2 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-blue-400 transition-colors"
                title="Edit plan"
              >
                <Edit className="w-4 h-4" />
              </button>
            )}
            {/* Duplicate Button */}
            {product && (
              <button
                type="button"
                onClick={() => {
                  setEditMode('duplicate');
                  setIsDuplicateModalOpen(true);
                }}
                className="inline-flex items-center justify-center p-2 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-green-400 transition-colors"
                title="Duplicate plan"
              >
                <Copy className="w-4 h-4" />
              </button>
            )}
            {/* Delete Button */}
            {product && onDelete && (
              <button
                type="button"
                onClick={() => onDelete(product.id)}
                className="inline-flex items-center justify-center p-2 rounded-lg border border-gray-600 bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-red-400 transition-colors"
                title="Delete plan"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="sticky top-0 z-10 px-6 border-b border-gray-700 bg-gray-800">
          <div className="flex gap-1">
            {/* Details Tab */}
            <button
              onClick={() => setActiveTab('details')}
              className={`px-4 py-3 flex-1 text-sm font-medium transition-colors hover:bg-gray-750 cursor-pointer relative ${activeTab === 'details'
                ? 'text-blue-400'
                : 'text-gray-400 hover:text-gray-200'
                }`}
            >
              Details
              {activeTab === 'details' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400" />
              )}
            </button>
            {/* If mode is not Work Order, show Procedures and Spare Parts Tabs */}
            {mode !== 'WO' ? (
              <>
                {/* Procedures Tab */}
                <button
                  onClick={() => setActiveTab('procedures')}
                  className={`px-4 py-3 flex-1 text-sm font-medium transition-colors hover:bg-gray-750 cursor-pointer relative ${activeTab === 'procedures'
                    ? 'text-blue-400'
                    : 'text-gray-400 hover:text-gray-200'
                    }`}
                >
                  Procedures
                  {activeTab === 'procedures' && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400" />
                  )}
                </button>
                {/* Spare Parts Tab */}
                <button
                  onClick={() => setActiveTab('spare-parts')}
                  className={`px-4 py-3 flex-1 text-sm font-medium transition-colors hover:bg-gray-750 cursor-pointer relative ${activeTab === 'spare-parts'
                    ? 'text-blue-400'
                    : 'text-gray-400 hover:text-gray-200'
                    }`}
                >
                  Spare Parts
                  {activeTab === 'spare-parts' && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400" />
                  )}
                </button>
              </>
            ) :
              /* If mode is Work Order, show Tasks and Assignments Tabs */
              (
                <>
                  {/* Tasks Tab */}
                  <button
                    onClick={() => setActiveTab('tasks')}
                    className={`px-4 py-3 flex-1 text-sm font-medium transition-colors hover:bg-gray-750 cursor-pointer relative ${activeTab === 'tasks'
                      ? 'text-blue-400'
                      : 'text-gray-400 hover:text-gray-200'
                      }`}
                  >
                    Tasks
                    {activeTab === 'tasks' && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400" />
                    )}
                  </button>
                  {/* Assignments Tab */}
                  <button
                    onClick={() => setActiveTab('assignments')}
                    className={`px-4 py-3 flex-1 text-sm font-medium transition-colors hover:bg-gray-750 cursor-pointer relative ${activeTab === 'assignments'
                      ? 'text-blue-400'
                      : 'text-gray-400 hover:text-gray-200'
                      }`}
                  >
                    Assignments
                    {activeTab === 'assignments' && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400" />
                    )}
                  </button>
                </>
              )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto p-6">
          {activeTab === 'details' ? (
            <div className="space-y-6">
              {/* Basic Information */}
              <section>
                <div className="flex gap-4 justify-between">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2 rounded-lg">
                    <Package className="w-5 h-5 text-gray-400" />
                    Basic Information
                  </h3>
                  {/* If mode is Work Order, show Status next to the Basic Information title*/}
                  {mode === 'WO' && (
                    <div className="flex items-center mb-4 gap-2">
                      <p className="text-blue-200/60 font-medium text-xs">Status</p>
                      <Badge className={
                        product.status?.toLowerCase() === 'completed' ? 'bg-green-900/50 text-green-200' :
                          product.status?.toLowerCase() === 'in_progress' ? 'bg-yellow-900/50 text-yellow-200' :
                            product.status?.toLowerCase() === 'cancelled' ? 'bg-red-900/50 text-red-200' :
                              'bg-blue-900/50 text-blue-200'
                      }>
                        {product.status?.charAt(0).toUpperCase() + product.status?.slice(1) || 'N/A'}
                      </Badge>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Name / Title */}
                  <div className="bg-gray-700/50 rounded-lg p-4 overflow-auto">
                    <p className="text-xs text-gray-400 mb-1">{mode !== 'WO' ? 'Name' : 'Title'}</p>
                    <p className="text-white font-medium overflow-auto">{mode !== 'WO' ? product.name || 'N/A' : product.title || 'N/A'}</p>
                  </div>
                  {/* Asset Name */}
                  <div className="bg-gray-700/50 rounded-lg p-4">
                    <p className="text-xs text-gray-400 mb-1">Asset</p>
                    <Badge className="bg-violet-900/50 text-violet-200">
                      {mode !== 'WO' ? nombres.nombre_asset || 'N/A' : product.asset_name || 'N/A'}
                    </Badge>
                  </div>
                  {/* Maintenance / Work Order Type */}
                  <div className="bg-gray-700/50 rounded-lg p-4">
                    <p className="text-xs text-gray-400 mb-1">{mode !== 'WO' ? 'Maintenance' : 'Work Order'} Type</p>
                    <p className="text-white">
                      {mode !== 'WO'
                        ? product.maintenance_type?.charAt(0).toUpperCase() + product.maintenance_type?.slice(1) || 'N/A'
                        : product.work_order_type?.charAt(0).toUpperCase() + product.work_order_type?.slice(1) || 'N/A'
                      }
                    </p>
                  </div>
                  {/* Priority */}
                  <div className="bg-gray-700/50 rounded-lg p-4">
                    <p className="text-xs text-gray-400 mb-1">Priority</p>
                    <Badge className={
                      product.priority?.toLowerCase() === 'critical' ? 'bg-red-900/50 text-red-200' :
                        product.priority?.toLowerCase() === 'high' ? 'bg-orange-900/50 text-orange-200' :
                          product.priority?.toLowerCase() === 'medium' ? 'bg-yellow-900/50 text-yellow-200' :
                            'bg-green-900/50 text-green-200'
                    }>
                      {product.priority?.charAt(0).toUpperCase() + product.priority?.slice(1) || 'N/A'}
                    </Badge>
                  </div>
                  {/* Description */}
                  {product.description && (
                    <div className="bg-gray-700/50 rounded-lg p-4 md:col-span-2">
                      <p className="text-xs text-gray-400 mb-1">Description</p>
                      <p className="text-white whitespace-pre-wrap">{product.description}</p>
                    </div>
                  )}
                </div>
                {/*Show preventive and predictive maintenance details if Plan is selected*/}
                {mode !== 'WO' && (
                  <div>
                    {product.maintenance_type === 'preventive' && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div className="bg-gray-700/50 rounded-lg p-4">
                          <p className="text-xs text-gray-400 mb-1">Next scheduled at:</p>
                          <p className="text-white">{dateFormat(product.next_scheduled) || 'N/A'}</p>
                        </div>
                        <div className="bg-gray-700/50 rounded-lg p-4">
                          <p className="text-xs text-gray-400 mb-1">Last executed at:</p>
                          <p className="text-white">{dateFormat(product.last_executed) || 'N/A'}</p>
                        </div>
                      </div>
                    )}
                    {product.maintenance_type === 'predictive' && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div className="bg-gray-700/50 rounded-lg p-4">
                          <p className="text-xs text-gray-400 mb-1">Condition:</p>
                          <p className="text-white overflow-auto">{product.condition_parameter || 'N/A'}</p>
                        </div>
                        <div className="bg-gray-700/50 rounded-lg p-4">
                          <p className="text-xs text-gray-400 mb-1">Threshold:</p>
                          <p className="text-white">{product.threshold_value || 'N/A'}</p>
                        </div>
                        <div className="bg-gray-700/50 rounded-lg p-4">
                          <p className="text-xs text-gray-400 mb-1">Sensor Asset:</p>
                          <Badge className="bg-pink-900/50 text-pink-200">
                            {nombres.nombre_sensor_asset || 'N/A'}
                          </Badge>
                        </div>
                        <div className="bg-gray-700/50 rounded-lg p-4">
                          <p className="text-xs text-gray-400 mb-1">Last executed at:</p>
                          <p className="text-white">{dateFormat(product.last_executed) || 'N/A'}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {/*Show work order schedule details if work order is selected*/}
                {mode === 'WO' && (
                  <div className="bg-gray-700/50 rounded-lg gap-4 mt-4 p-4">
                    <p className="text-xs text-gray-400 mb-1">Scheduled for:</p>
                    <p className="text-white">{dateFormat(product.scheduled_date) || 'N/A'}</p>
                  </div>
                )}

                {/*Estimated Duration + Created at + Updated at + Created by*/}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="bg-gray-700/50 rounded-lg p-4 md:col-span-2">
                    <p className="text-xs text-gray-400 mb-1">Estimated Duration</p>
                    <p className="text-white">
                      {mode !== 'WO' ? `${product.duration_minutes} minutes` : `${product.estimated_hours} hours`}
                    </p>
                  </div>
                  <div className="bg-gray-700/50 rounded-lg p-4">
                    <p className="text-xs text-gray-400 mb-1">Created at:</p>
                    <p className="text-white">{dateFormat(product.created_at) || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-700/50 rounded-lg p-4">
                    <p className="text-xs text-gray-400 mb-1">Updated at:</p>
                    <p className="text-white">{dateFormat(product.updated_at) || 'N/A'}</p>
                  </div>
                  {mode === 'WO' && (
                    <div className="bg-gray-700/50 rounded-lg p-4">
                      <p className="text-xs text-gray-400 mb-1">Created by:</p>
                      <p className="text-white">{product.creator_name}</p>
                    </div>
                  )}
                </div>
              </section>
            </div>
          ) : mode !== 'WO' ? (
            /* Call Maintenance Plan other Tabs */
            activeTab === 'procedures' ? (
              <PlansProcedures planId={product.id} />
            ) : (
              <PlansSpareParts planId={product.id} />
            )
          ) : activeTab === 'tasks' ? (
            /* Call Work Order other Tabs */
            <WorkOrdersTasks workOrder={product}
              dateFormat={dateFormat}
              onUpdate={onSaveUpdate}
            />
          ) : (
            <WorkOrdersAssignments workOrder={product} dateFormat={dateFormat} />
          )}
        </div>
      </div>

      {/* Edit/Duplicate Modal */}
      {(isEditModalOpen || isDuplicateModalOpen) && (
        mode === 'maintPlan' ? (
          <PlansEditModal
            isOpen={isEditModalOpen || isDuplicateModalOpen}
            onClose={() => {
              setIsEditModalOpen(false);
              setIsDuplicateModalOpen(false);
            }}
            product={product}
            mode={editMode}
            onSave={async (productData) => {
              if (editMode === 'duplicate') {
                await onSaveDuplicate(productData);
              } else {
                await onSaveUpdate(productData);
              }
              setIsEditModalOpen(false);
              setIsDuplicateModalOpen(false);
            }}
          />
        ) : (
          <WorkOrdersEditModal
            isOpen={isEditModalOpen || isDuplicateModalOpen}
            onClose={() => {
              setIsEditModalOpen(false);
              setIsDuplicateModalOpen(false);
            }}
            workOrder={product}
            maintenancePlan={maintenancePlan}
            mode={editMode}
            onSave={async (productData) => {
              if (editMode === 'duplicate') {
                await onSaveDuplicate(productData);
              } else {
                await onSaveUpdate(productData);
              }
              setIsEditModalOpen(false);
              setIsDuplicateModalOpen(false);
            }}
          />
        )
      )}
    </>
  );
};